import React, { useContext, useEffect } from "react";
import { useTour } from "../components/ui/TourContext";
import AppRoutes from "./app-route";
import AuthContext from "../hooks/authContext";
import { ENABLE_GUIDED_TOUR } from "../services/featureFlags";

function AuthenticatedApp({ props, isLoading, path, guidedTourEligibility }) {
  const { stopTour, isActive, currentTourId } = useTour();
  // To re-enable auto-start tours in future:
  // 1) change the line above to:
  // const { startTour, stopTour, isActive, currentTourId } = useTour();
  // 2) uncomment the block in this file marked:
  //    "AUTO-START TOUR (DISABLED)"
  const auth = useContext(AuthContext);

  useEffect(() => {
    if (!ENABLE_GUIDED_TOUR) {
      localStorage.removeItem("startTour");
      return;
    }

    const blockedRoutes = [
      "/",
      "/login",
      "/register",
      "/verify-email-sent",
      "/verify-email",
      "/verify-new-email",
      "/updatepassword",
      "/complete-registration",
      "/forgotpassword",
    ];
    if (isLoading || auth?.isLoading || auth?.status !== true) return;
    if (blockedRoutes.includes(path)) return;

    const userId = auth?.userProfile?.id;
    const tourKey = userId ? `hasSeenTour_${userId}` : "hasSeenTour";
    // Only use user-scoped key when we have userId so a new account never inherits
    // "has seen tour" from a previous user's global localStorage.
    const hasSeenTour = userId
      ? localStorage.getItem(tourKey) === "true"
      : localStorage.getItem("hasSeenTour") === "true";

    if (hasSeenTour) {
      // Clean up any stray start flags
      localStorage.removeItem("startTour");
      return;
    }

    if (!guidedTourEligibility?.isResolved) return;

    const hasClients = guidedTourEligibility?.hasClients;
    const hasEmployees = guidedTourEligibility?.hasEmployees;
    const hasAppointments = guidedTourEligibility?.hasAppointments;
    const hasAnyData = hasClients || hasEmployees || hasAppointments;

    if (hasAnyData) {
      if (userId) localStorage.setItem(tourKey, "true");
      localStorage.setItem("hasSeenTour", "true"); // Backward compatibility
      localStorage.removeItem("startTour");

      // If the tour is currently active and it's the getting-started one, stop it
      if (isActive && currentTourId === "getting-started") {
        stopTour();
      }
      return;
    }

    // Do not auto-start guided tours on load.
    // Tours should only start from explicit user actions in the Tour page.
    if (path === "/dashboard") {
      localStorage.removeItem("startTour");
    }

    /*
     * AUTO-START TOUR (DISABLED)
     * Uncomment to restore previous onboarding tour behavior.
     *
     * // Only auto-start the getting-started tour on the dashboard for brand new accounts.
     * // Persist that we've shown (or are showing) the tour for this user so we don't
     * // show again on refresh during the same session or future sessions.
     * if (path === "/dashboard") {
     *   if (userId) localStorage.setItem(tourKey, "true");
     *   localStorage.setItem("startTour", "getting-started");
     *   startTour("getting-started");
     * }
     */
  }, [isLoading, path, guidedTourEligibility, stopTour, isActive, currentTourId, auth?.isLoading, auth?.status, auth?.userProfile?.id]);

  return <AppRoutes {...props} isLoading={isLoading} />;
}

export default AuthenticatedApp;
